import React, { useState, useEffect, useCallback } from "react";
import { Col, Container, Row, Card, CardBody } from "reactstrap";
import { Button, Form } from "react-bootstrap";
import { useLocation, useHistory } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import config from "../../../../config";
import Loader from "../../../Helper/Loader";
import Breadcrumbs from "../../../Helper/Breadcrumbs";
import empty from "is-empty";
import { compressImage, validateImageFile } from "../../../Helper/imageCompression";

const slugify = (str) =>
  (str || "")
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-");

const BlogAuthorsForm = () => {
  const location = useLocation();
  const history = useHistory();
  const { id, editMode } = location.state || {};

  const tokenKey = {
    Authorization: "Token " + sessionStorage.getItem("tokenKey"),
    "Content-Type": "multipart/form-data",
  };

  const toastAttr = {
    theme: "colored",
    position: "top-center",
    autoClose: 1000,
    hideProgressBar: true,
  };

  const breadcrumbData = [
    { title: "Seobook", href: "/seobook" },
    { title: "Blog Authors", href: "/seobook/blogAuthors" },
    { title: editMode ? "Edit" : "Create", href: "#" },
  ];

  const [form, setForm] = useState({
    name: "",
    slug: "",
    designation: "",
    short_bio: "",
    bio: "",
    expertise: "",
    qualifications: "",
    years_of_experience: "",
    linkedin_url: "",
    instagram_url: "",
    twitter_url: "",
    facebook_url: "",
    youtube_url: "",
    pinterest_url: "",
    tiktok_url: "",
    website_url: "",
    email: "",
    is_active: true,
    meta_title: "",
    meta_description: "",
    profile_image: null,
  });

  const [file, setFile] = useState();
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [slugTouched, setSlugTouched] = useState(false);

  useEffect(() => {
    const fetchAuthor = async () => {
      if (editMode && id) {
        try {
          const res = await axios.get(
            `${config.service}/masters/blog-authors/${id}/`,
            { headers: tokenKey }
          );
          setForm((prev) => ({ ...prev, ...res.data }));
          setSlugTouched(true);
        } catch (err) {
          toast.error("Failed to fetch author data", toastAttr);
        }
      }
      setLoading(false);
    };
    fetchAuthor();
  }, [editMode, id]);

  const handleInput = useCallback(
    (key, value) => {
      setForm((prev) => {
        const next = { ...prev, [key]: value };
        if (key === "name" && !slugTouched) next.slug = slugify(value);
        return next;
      });
      if (errors[key]) setErrors((prev) => ({ ...prev, [key]: null }));
    },
    [errors, slugTouched]
  );

  const handleImageSelect = useCallback(async (selectedFile) => {
    if (!selectedFile) return;

    const { valid, error } = validateImageFile(selectedFile);
    if (!valid) {
      toast.error(error, toastAttr);
      return;
    }

    const optimized = await compressImage(selectedFile);
    setFile(URL.createObjectURL(optimized));
    setForm((prev) => ({ ...prev, profile_image: optimized }));
    setErrors((prev) => ({ ...prev, profile_image: null }));
  }, []);

  const validateForm = useCallback(() => {
    const newErrors = {};
    if (empty(form.name)) newErrors.name = "Author name is required";
    return newErrors;
  }, [form]);

  const renderListView = useCallback(() => {
    history.push("/seobook/blogAuthors");
  }, [history]);

  const saveForm = useCallback(async () => {
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error("Enter all required details to proceed", toastAttr);
      return;
    }

    setLoading(true);

    const url = editMode
      ? `${config.service}/masters/blog-authors/${id}/`
      : `${config.service}/masters/blog-authors/`;
    const method = editMode ? "put" : "post";

    const formData = new FormData();
    Object.entries(form).forEach(([key, value]) => {
      if (key === "profile_image") {
        if (value instanceof File) formData.append("profile_image", value);
      } else if (value !== null && value !== undefined) {
        formData.append(
          key,
          typeof value === "boolean" ? (value ? "true" : "false") : value
        );
      }
    });

    try {
      await axios({ url, method, headers: tokenKey, data: formData });
      toast.success(
        editMode ? "Updated successfully" : "Created successfully",
        toastAttr
      );
      renderListView();
    } catch (err) {
      console.error("API Error:", err);
      if (err.response?.status === 401) window.location.href = "/";
      else {
        const fieldErrors = err.response?.data;
        if (fieldErrors && typeof fieldErrors === "object")
          setErrors(fieldErrors);
        toast.error("Saving Failed", toastAttr);
      }
    } finally {
      setLoading(false);
    }
  }, [form, editMode, id, renderListView]);

  const textFields = [
    { label: "Designation", name: "designation", type: "text", placeholder: "e.g. Senior Content Strategist" },
    { label: "Short Bio", name: "short_bio", type: "textarea", rows: 2, placeholder: "1-2 line summary shown on bylines and cards (~160 chars)" },
    { label: "Full Bio", name: "bio", type: "textarea", rows: 5, placeholder: "Detailed background, experience, and areas of expertise" },
    { label: "Expertise", name: "expertise", type: "text", placeholder: "Comma-separated, e.g. Wedding Planning, Venue Curation, Photography" },
    { label: "Qualifications", name: "qualifications", type: "textarea", rows: 2, placeholder: "Certifications, education, credentials" },
    { label: "Years of Experience", name: "years_of_experience", type: "number" },
    { label: "LinkedIn URL", name: "linkedin_url", type: "url" },
    { label: "Instagram URL", name: "instagram_url", type: "url" },
    { label: "Twitter / X URL", name: "twitter_url", type: "url" },
    { label: "Facebook URL", name: "facebook_url", type: "url" },
    { label: "YouTube URL", name: "youtube_url", type: "url" },
    { label: "Pinterest URL", name: "pinterest_url", type: "url" },
    { label: "TikTok URL", name: "tiktok_url", type: "url" },
    { label: "Website URL", name: "website_url", type: "url" },
    { label: "Email", name: "email", type: "email" },
    { label: "Meta Title", name: "meta_title", type: "text" },
    { label: "Meta Description", name: "meta_description", type: "textarea", rows: 3 },
  ];

  return (
    <Container className="vendor">
      <Row>
        <Col md={12} className="contBody">
          <Card>
            <CardBody>
              <div className="card__title pageNav">
                <Breadcrumbs data={breadcrumbData} />
              </div>

              <Row className="formBtnsDiv mb-4">
                <Col md={12} className="d-flex gap-2">
                  <Button variant="primary" onClick={saveForm} className="px-4">
                    {editMode ? "Update" : "Save"}
                  </Button>
                  <Button variant="outline-secondary" onClick={renderListView}>
                    Cancel
                  </Button>
                </Col>
              </Row>

              <Loader loading={loading}>
                <Form className="form defaultForm">
                  <Row className="mb-3 align-items-center">
                    <Col md={2}>
                      <Form.Label className="fw-bold">Name *</Form.Label>
                    </Col>
                    <Col md={8}>
                      <Form.Control
                        type="text"
                        value={form.name || ""}
                        onChange={(e) => handleInput("name", e.target.value)}
                        isInvalid={!!errors.name}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.name}
                      </Form.Control.Feedback>
                    </Col>
                  </Row>

                  <Row className="mb-3 align-items-center">
                    <Col md={2}>
                      <Form.Label className="fw-bold">Slug</Form.Label>
                    </Col>
                    <Col md={8}>
                      <Form.Control
                        type="text"
                        value={form.slug || ""}
                        onChange={(e) => {
                          setSlugTouched(true);
                          handleInput("slug", e.target.value);
                        }}
                        isInvalid={!!errors.slug}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.slug}
                      </Form.Control.Feedback>
                    </Col>
                  </Row>

                  {/* Profile Image */}
                  <Row className="mb-3">
                    <Col md={2}>
                      <Form.Label className="fw-bold">Profile Image</Form.Label>
                    </Col>
                    <Col md={8}>
                      <Form.Control
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        onChange={(e) => {
                          handleImageSelect(e.target.files[0]);
                          e.target.value = "";
                        }}
                        isInvalid={!!errors.profile_image}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.profile_image}
                      </Form.Control.Feedback>
                    </Col>
                  </Row>

                  {(file || form.profile_image) && (
                    <Row className="mb-4">
                      <Col md={{ offset: 2, size: 8 }}>
                        <div className="image-preview-container p-3 border rounded">
                          <p className="text-muted mb-2">Image Preview:</p>
                          <img
                            src={file || form.profile_image}
                            className="rounded-circle"
                            style={{ width: "80px", height: "80px", objectFit: "cover" }}
                            alt="Preview"
                          />
                        </div>
                      </Col>
                    </Row>
                  )}

                  {textFields.map((field) => (
                    <Row key={field.name} className="mb-3 align-items-center">
                      <Col md={2}>
                        <Form.Label className="fw-bold">{field.label}</Form.Label>
                      </Col>
                      <Col md={8}>
                        <Form.Control
                          as={field.type === "textarea" ? "textarea" : "input"}
                          type={field.type === "textarea" ? undefined : field.type}
                          rows={field.rows}
                          placeholder={field.placeholder}
                          value={form[field.name] || ""}
                          onChange={(e) => handleInput(field.name, e.target.value)}
                          isInvalid={!!errors[field.name]}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors[field.name]}
                        </Form.Control.Feedback>
                      </Col>
                    </Row>
                  ))}

                  <Row className="mb-3 align-items-center">
                    <Col md={2}>
                      <Form.Label className="fw-bold">Active</Form.Label>
                    </Col>
                    <Col md={8}>
                      <Form.Check
                        type="switch"
                        checked={!!form.is_active}
                        onChange={(e) => handleInput("is_active", e.target.checked)}
                        label={form.is_active ? "Visible on the website" : "Hidden from the website"}
                      />
                    </Col>
                  </Row>
                </Form>
              </Loader>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default BlogAuthorsForm;
